import {
  IService,
  IServiceAddOn,
  IServiceMachineryCompatibility,
  ICreateServiceRequest,
  IUpdateServiceRequest,
  ICreateServiceMachineryCompatibilityRequest,
  ICreateServiceAddOnRequest,
  IUpdateServiceAddOnRequest,
  IServiceSearchFilters,
  IServiceSearchResponse,
  IServiceDetail,
  ICreateServiceBookingRequest,
  ICreateServiceBookingResponse,
} from '../interfaces/heavyDutyMachinery/IService';
import { apiService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import { getLocalStorageKey } from '../helpers/localStorageHelper';

// Same separate-host convention as HeavyDutyMachineryService's own heavyDutyMachineryBaseUrl.
const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

/** Backend's own sentinel for "No service provider profile yet" -- see ServiceController's own doc comment (a caller needs one before touching this catalog at all). Matched by callers wanting a friendlier message than the raw one below. */
export const NO_SERVICE_PROVIDER_PROFILE_ERROR = 'No service provider profile yet';

/**
 * Guest-facing, [AllowAnonymous] -- see ServiceController.SearchServices.
 *
 * This is the map's own search now, which is what it was always kept here
 * for: the standalone ServicesSearch page was removed on purpose because
 * service discovery belongs on the map (see the Fase 13 architecture doc).
 */
export const searchServices = async (filters: IServiceSearchFilters): Promise<IServiceSearchResponse> => {
  const params: Record<string, string | number> = { page: filters.page, pageSize: filters.pageSize };
  if (filters.countryCode) params.countryCode = filters.countryCode;
  if (filters.serviceCategoryId != null) params.serviceCategoryId = filters.serviceCategoryId;
  if (filters.requestMode) params.requestMode = filters.requestMode;
  if (filters.pricingMode) params.pricingMode = filters.pricingMode;
  // All four or none -- see IServiceSearchFilters. Sending three would be
  // silently ignored by the server, which is worse than not sending them.
  if (filters.minLat != null && filters.maxLat != null && filters.minLng != null && filters.maxLng != null) {
    params.minLat = filters.minLat;
    params.maxLat = filters.maxLat;
    params.minLng = filters.minLng;
    params.maxLng = filters.maxLng;
  }
  // Both or neither -- see IServiceSearchFilters' own doc comment.
  if (filters.availabilityStart && filters.availabilityEnd) {
    params.availabilityStart = filters.availabilityStart;
    params.availabilityEnd = filters.availabilityEnd;
  }

  const response = await apiService<IServiceSearchResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICES_SEARCH,
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to search services');
};

/** Guest-facing single-Service detail, [AllowAnonymous] -- see ServiceController.GetPublicService. */
export const getPublicService = async (id: string): Promise<IServiceDetail> => {
  const response = await apiService<IServiceDetail>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_PUBLIC_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the service');
};

/**
 * A standalone booking for one Service (plus its own selected add-ons) --
 * see BookingController.CreateServiceBooking. Returns the same
 * clientSecret shape createBooking does, feeding straight into
 * BookingPaymentStep (see HeavyDutyMachineryBookingWidget) -- no separate
 * Service-specific payment flow, same Stripe Payment Element mechanism.
 */
export const createServiceBooking = async (request: ICreateServiceBookingRequest): Promise<ICreateServiceBookingResponse> => {
  const response = await apiService<ICreateServiceBookingResponse>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_BOOKINGS,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create the booking');
};

/** A caller's own Service catalog -- see ServiceController.GetMyServices. */
export const getMyServices = async (): Promise<IService[]> => {
  const response = await apiService<IService[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load your services');
};

export const getServiceById = async (id: string): Promise<IService> => {
  const response = await apiService<IService>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the service');
};

export const createService = async (request: ICreateServiceRequest): Promise<IService> => {
  const response = await apiService<IService>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICES,
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create the service');
};

/** Also how a provider pauses/resumes one (request.active = false/true) -- pausing is still the right choice while it has booking history, since deleteService below blocks exactly that. */
export const updateService = async (id: string, request: IUpdateServiceRequest): Promise<IService> => {
  const response = await apiService<IService>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_BY_ID(id),
    method: HTTP_METHODS.PUT,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update the service');
};

/** Real delete -- rejected by the backend with a friendly message if the service has booking history; deactivate it instead (see updateService). */
export const deleteService = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete the service');
};

/** Only allowed when the Service's own category has attachableToMachineryBooking -- see ServiceController.AddMachineryCompatibility. */
export const addServiceMachineryCompatibility = async (
  serviceId: string,
  request: ICreateServiceMachineryCompatibilityRequest
): Promise<IServiceMachineryCompatibility> => {
  const response = await apiService<IServiceMachineryCompatibility>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITIES(serviceId),
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to add the compatibility');
};

/** Raw fetch, not apiService -- ServiceController.RemoveMachineryCompatibility returns a bare 204 No Content (unlike every other DELETE in this app, which always sends a JSON body), and apiService's own response.json() would throw on that empty body. */
export const removeServiceMachineryCompatibility = async (serviceId: string, compatibilityId: string): Promise<void> => {
  const accessToken = getLocalStorageKey('accessToken');
  const response = await fetch(
    `${heavyDutyMachineryBaseUrl}${API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_COMPATIBILITY_BY_ID(serviceId, compatibilityId)}`,
    {
      method: HTTP_METHODS.DELETE,
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    }
  );
  if (!response.ok) throw new Error(`Failed to remove the compatibility (HTTP ${response.status})`);
};

export const createServiceAddOn = async (serviceId: string, request: ICreateServiceAddOnRequest): Promise<IServiceAddOn> => {
  const response = await apiService<IServiceAddOn>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ONS(serviceId),
    method: HTTP_METHODS.POST,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to create the add-on');
};

/** Also how a provider pauses one (request.active = false) -- there is no separate hard-delete endpoint. */
export const updateServiceAddOn = async (
  serviceId: string,
  addOnId: string,
  request: IUpdateServiceAddOnRequest
): Promise<IServiceAddOn> => {
  const response = await apiService<IServiceAddOn>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_SERVICE_ADD_ON_BY_ID(serviceId, addOnId),
    method: HTTP_METHODS.PUT,
    body: { ...request },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update the add-on');
};
